import React, { useCallback, useEffect, useState } from 'react';
import { useLongPress, LongPressDetectEvents } from 'use-long-press';

/* MUI */
import {
  Dialog,
  DialogContent,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Popover,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
} from '@mui/material';
import { BottomBreadcrumbs } from './bottom-breadcrumbs';
import { useIsSmallScreen } from '../../system/SystemMediaQueriesProvider';

/* COMPONENTS */
import {
  BottomNavigation,
  BottomNavigationAction,
  PrimaryBottomNavigationComponent,
} from './components/bottom-navigation';
import { Notifier } from '../../system/services/notificationManager';

/* ICONS */
import { Icon } from '@iconify/react';

import { useNavigationBlocker } from '../../system/services/navigationBlockerContext';
import {
  useInjectableComponents,
  BottomMenu,
  ConfirmableAction,
} from '../../system/services/injectableComponentsManager';
import { useSystemHistory } from '../history/history-manager';
import ListItemButton from '@mui/material/ListItemButton';
import { humanize } from '../shell/utilities';

export interface BreadcrumbDialogProps {
  anchorHistoryEl?: HTMLElement | null;
  onClose: (value: string) => void;
  open: boolean;
}

export interface SharedDialogProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  handleCloseMenu: () => void;
}

function BreadcrumbDialog(props: BreadcrumbDialogProps) {
  const { onClose, anchorHistoryEl, open } = props;

  return (
    <Dialog onClose={onClose} open={open}>
      <BottomBreadcrumbs
        open={open}
        anchorHistoryEl={anchorHistoryEl}
        onClose={onClose}
      />
    </Dialog>
  );
}

function InjectableBottomMenu(
  props: SharedDialogProps & {
    menu: BottomMenu;
  },
) {
  const { open, anchorEl, handleCloseMenu, menu } = props;
  const { items } = menu;
  const [showDialog, setShowDialog] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [confirmableAction, setConfirmableAction] =
    useState<ConfirmableAction>();

  // Update confirmable action any time a different menu item is selected, or the selected item has any changes.
  // The selected item might change if it has a confirmable action that includes any custom content to render.
  useEffect(() => {
    if (selectedItemId) {
      items.forEach((item) => {
        item.items.forEach((subItem) => {
          if (subItem.id === selectedItemId) {
            setConfirmableAction(subItem.confirmableAction);
          }
        });
      });
    } else {
      setConfirmableAction(undefined);
    }
  }, [items, selectedItemId]);

  return (
    <Popover
      open={open}
      onClose={() => {
        handleCloseMenu();
        setSelectedItemId('');
        setConfirmableAction(undefined);
      }}
      keepMounted
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
    >
      <Paper sx={{ width: 320, maxWidth: '100%' }}>
        <MenuList>
          {items.map((item, index) => {
            const res = [];
            if (
              item.showDivider ||
              ((index > 0 || item.label) && false !== item.showDivider)
            ) {
              res.push(
                item.label ? (
                  <Divider
                    key={`menu-divider-${index}`}
                    sx={{
                      fontSize: '14px',
                      fontWeight: 'bold',
                      color: 'lightblue',
                    }}
                    textAlign="left"
                  >
                    {item.label}
                  </Divider>
                ) : (
                  <Divider key={`menu-divider-${index}`} />
                ),
              );
            }

            item.items.forEach((subItem, subIndex) => {
              let action: () => void;
              if (subItem.confirmableAction) {
                action = () => {
                  setSelectedItemId(subItem.id);
                  setShowDialog(true);
                };
              } else {
                action = subItem.action ?? (() => {});
              }
              res.push(
                <MenuItem
                  key={`menu-${index}-${subItem.id ?? subIndex}`}
                  id={subItem.id}
                  onClick={() => {
                    action();
                    handleCloseMenu();
                  }}
                >
                  {subItem.icon && <ListItemIcon>{subItem.icon}</ListItemIcon>}
                  <ListItemText>{subItem.label}</ListItemText>
                </MenuItem>,
              );
            });

            return res;
          })}
        </MenuList>
        {confirmableAction && (
          <ActionMenuConfirmationDialog
            confirmableAction={confirmableAction}
            open={showDialog}
            handleClose={() => setShowDialog(false)}
          />
        )}
      </Paper>
    </Popover>
  );
}

const ActionMenuConfirmationDialog = ({
  confirmableAction,
  open,
  handleClose,
}: {
  confirmableAction: ConfirmableAction;
  open: boolean;
  handleClose: () => void;
}) => {
  const { title, action, prompt, actionLabel, cancelLabel, customContent } =
    confirmableAction;

  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <Typography variant="h4" sx={{ padding: '15px' }}>
          {title}
        </Typography>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {prompt}
          </DialogContentText>
          {customContent && <Box sx={{ mt: 2 }}>{customContent}</Box>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{cancelLabel}</Button>
          <Button
            variant="contained"
            color="secondary"
            sx={{ color: '#fff' }}
            onClick={async () => {
              try {
                await action();
                handleClose();
              } catch (error) {
                if (error instanceof Error) {
                  Notifier.error(error.message);
                } else {
                  Notifier.error('Unknown error while performing action');
                }
              }
            }}
            autoFocus
          >
            {actionLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const SecondaryActionIcon = () => {
  const { secondaryNavigationMenu } = useInjectableComponents();

  if (secondaryNavigationMenu) {
    return (
      <Icon
        icon="solar:hamburger-menu-bold"
        fontSize="30px"
        style={{ pointerEvents: 'none' }}
      />
    );
  } else {
    return null;
  }
};

interface SecondaryActionMenuProps {
  anchorEl: any;
  handleCloseMenu: () => void;
}

const SecondaryActionMenu = (props: SecondaryActionMenuProps) => {
  const { anchorEl, handleCloseMenu } = props;
  const { secondaryNavigationMenu } = useInjectableComponents();
  const open = Boolean(anchorEl);

  if (secondaryNavigationMenu) {
    return (
      <InjectableBottomMenu
        open={open}
        anchorEl={anchorEl}
        handleCloseMenu={handleCloseMenu}
        menu={secondaryNavigationMenu}
      />
    );
  } else {
    return null;
  }
};

// interface BottomNavbarProps {
//   handleRouteChange: (route: string) => void;
//   primaryNavigationAction?: React.ReactNode;
//   secondaryNavigationAction: {
//     value: any;
//     icon: React.ReactNode;
//   };
// }

function HistoryMenuDialog({
  open,
  onClose,
  onSelect,
}: {
  open: boolean;
  onClose: () => void;
  onSelect: (index: number) => void;
}) {
  const { clear, stack, currentIndex } = useSystemHistory();
  const handleClearHistory = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.stopPropagation();
      event.preventDefault();
      await clear();
      onClose();
    },
    [clear, onClose],
  );

  const handleCloseHistory = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.stopPropagation();
      event.preventDefault();
      onClose();
    },
    [clear, onClose],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          margin: 0,
          borderRadius: '16px 16px 0 0',
        },
      }}
    >
      <DialogContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Navigation History
        </Typography>
        <List>
          {stack
            .slice()
            .reverse()
            .map((entry, displayIndex) => {
              // Convert display index (reversed) back to original stack index
              // displayIndex 0 = most recent entry (last in original stack)
              const originalIndex = stack.length - 1 - displayIndex;
              const entryLabel = entry[1]
                ? entry[1]
                : entry[0]
                  ? humanize(
                      entry[0].charAt(0).toUpperCase() + entry[0].slice(1),
                    )
                  : 'Untitled Page';
              return (
                <ListItemButton
                  key={originalIndex}
                  onClick={() => onSelect(originalIndex)}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                    border:
                      originalIndex === currentIndex
                        ? '1px solid green'
                        : 'none',
                  }}
                >
                  <ListItemText primary={entryLabel} />
                  {/*secondary={entry[0]} /> */}
                </ListItemButton>
              );
            })}
        </List>
        <Button
          variant="contained"
          style={{
            marginTop: '20px',
            marginBottom: '10px',
          }}
          onClick={handleClearHistory}
        >
          Clear History
        </Button>
        <Button
          variant="contained"
          style={{
            marginLeft: '10px',
            marginTop: '20px',
            marginBottom: '10px',
          }}
          onClick={handleCloseHistory}
        >
          Close History
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export function BottomNavbar() {
  // const [value, setValue] = useState('');
  const [showHistoryMenu, setShowHistoryMenu] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [anchorHistoryEl, setAnchorHistoryEl] = useState<HTMLElement | null>(
    null,
  );

  const isSmall = useIsSmallScreen();

  const { back, forward, go, hasPrevious, hasNext, currentIndex } =
    useSystemHistory();
  const { navigateWithInterceptor } = useNavigationBlocker();
  const { primaryNavigationWidget } = useInjectableComponents();

  // const handleOpenHistoryMenu = (event: React.MouseEvent<HTMLElement>) => {
  //   setAnchorHistoryEl(event.currentTarget);
  // };

  const handleCloseHistoryMenu = () => {
    setAnchorHistoryEl(null);
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleHistorySelect = async (index: number) => {
    await go(index);
    setShowHistoryMenu(false);
  };

  const longPressBind = useLongPress(
    () => {
      setShowHistoryMenu(true);
    },
    {
      threshold: 200,
      captureEvent: true,
      cancelOnMovement: true,
      detect: LongPressDetectEvents.BOTH,
    },
  );
  // const bind = useLongPress((event: any) => handleOpenHistoryMenu(event), {
  //   onStart: () => enableCrumbs(true),
  //   onFinish: () => enableCrumbs(false),
  //   threshold: 500,
  //   captureEvent: true,
  //   cancelOnMovement: false,
  //   detect: LongPressDetectEvents.BOTH,
  // });

  const openBreadcrumbDialog = Boolean(anchorHistoryEl);

  return (
    <BottomNavigation
      aria-label="bottom navigation"
      sx={
        isSmall ? { height: '80px', paddingBottom: '17px' } : { position: '' }
      }
    >
      <BottomNavigationAction
        aria-label="back"
        style={{
          maxWidth: '120px',
          minWidth: isSmall ? '60px' : 'inherit',
          borderLeft: isSmall ? '1px solid lightgray' : '1px solid lightgrey',
        }}
        disabled={!hasPrevious}
        {...longPressBind()}
        onClick={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          await back();
        }}
        showLabel
        icon={<Icon icon="ion:arrow-back-circle-sharp" fontSize="30px" />}
        label="Back"
      />
      <BottomNavigationAction
        aria-label="forward"
        style={{
          maxWidth: '120px',
          borderLeft: '1px solid lightgrey',
          minWidth: isSmall ? '60px' : 'inherit',
        }}
        disabled={!hasNext}
        onClick={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          await forward();
        }}
        showLabel
        icon={<Icon icon="ion:arrow-forward-circle-sharp" fontSize="30px" />}
        label="Forward"
      />
      {primaryNavigationWidget ? (
        <PrimaryBottomNavigationComponent>
          {primaryNavigationWidget}
        </PrimaryBottomNavigationComponent>
      ) : (
        <BottomNavigationAction
          aria-label="properties"
          style={{
            flex: isSmall ? '1 1 auto' : '0 1 auto',
            borderLeft: '1px solid lightgrey',
            minWidth: '100px',
          }}
          label="Properties"
          onClick={() => {
            // TODO: Peter: this should actually navigate to the user's preferred home page.
            navigateWithInterceptor('/properties');
          }}
          showLabel
          value="properties"
          icon={<Icon icon="mdi:home-circle" fontSize="30px" />}
        />
      )}
      <BreadcrumbDialog
        open={openBreadcrumbDialog}
        anchorHistoryEl={anchorHistoryEl}
        onClose={handleCloseHistoryMenu}
      />
      <SecondaryActionMenu
        anchorEl={anchorEl}
        handleCloseMenu={handleCloseMenu}
      />
      <BottomNavigationAction
        aria-label="menu"
        onClick={handleOpenMenu}
        style={{
          minWidth: isSmall ? '65px' : 'inherit',
          borderLeft: '1px solid lightgrey',
          borderRight: isSmall ? '1px solid lightgray' : '1px solid lightgrey',
        }}
        showLabel
        label="Menu"
        value={null}
        icon={<SecondaryActionIcon />}
      />
      <HistoryMenuDialog
        open={showHistoryMenu}
        onClose={() => setShowHistoryMenu(false)}
        onSelect={handleHistorySelect}
      />
    </BottomNavigation>
  );
}
